package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveTimer
import fr.manyverse.app.domain.model.GmTimer
import fr.manyverse.app.domain.usecase.CreateTimerUseCase
import fr.manyverse.app.domain.usecase.GetActiveTimersUseCase
import fr.manyverse.app.domain.usecase.ObserveActiveTimersUseCase
import fr.manyverse.app.domain.usecase.PauseTimerUseCase
import fr.manyverse.app.domain.usecase.ResumeTimerUseCase
import fr.manyverse.app.domain.usecase.StopTimerUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * ViewModel for managing timer state and actions.
 */
class TimerViewModel(
    private val createTimerUseCase: CreateTimerUseCase,
    private val stopTimerUseCase: StopTimerUseCase,
    private val observeActiveTimersUseCase: ObserveActiveTimersUseCase,
    private val getActiveTimersUseCase: GetActiveTimersUseCase,
    private val pauseTimerUseCase: PauseTimerUseCase,
    private val resumeTimerUseCase: ResumeTimerUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _launchState = MutableStateFlow<AsyncUiState<ActiveTimer>>(AsyncUiState.Idle)
    val launchState: StateFlow<AsyncUiState<ActiveTimer>> = _launchState.asStateFlow()

    private val _activeTimers = MutableStateFlow<List<ActiveTimer>>(emptyList())
    val activeTimers: StateFlow<List<ActiveTimer>> = _activeTimers.asStateFlow()

    private val _gmTimers = MutableStateFlow<List<GmTimer>>(emptyList())
    val gmTimers: StateFlow<List<GmTimer>> = _gmTimers.asStateFlow()

    private var observeTimersJob: Job? = null
    private var observingTableId: String? = null
    private var observingUserId: String? = null

    /**
     * Creates a new timer (GM action).
     */
    fun createTimer(
        tableId: String,
        durationSeconds: Int,
        targetParticipants: List<String>,
        createdBy: String
    ) {
        launchAsyncWithCallback(
            state = _launchState,
            block = {
                createTimerUseCase(
                    tableId = tableId,
                    durationSeconds = durationSeconds,
                    targetParticipants = targetParticipants,
                    createdBy = createdBy
                )
            },
            onSuccess = { timer ->
                // Add to local active timers list for GM view
                _activeTimers.value += timer
            }
        )
    }

    /**
     * Stops a timer (GM action).
     */
    fun stopTimer(
        timerId: String
    ) {
        viewModelScope.launch {
            stopTimerUseCase(
                timerId = timerId
            ).onSuccess {
                // Remove from local active timers list
                _activeTimers.value = _activeTimers.value.filter { it.id != timerId }
            }.onFailure(errorNotifier::notify)
        }
    }

    /**
     * Pauses a running timer or resumes a paused one (GM action).
     * Players receive the change through Realtime.
     */
    fun togglePauseTimer(
        timerId: String
    ) {
        val timer = _activeTimers.value.find { it.id == timerId } ?: return
        viewModelScope.launch {
            val result = if (timer.isPaused) resumeTimerUseCase(timerId) else pauseTimerUseCase(timerId)
            result.onSuccess { updated ->
                // GM list is not fed by Realtime: replace the timer locally
                _activeTimers.update { timers -> timers.map { if (it.id == timerId) updated else it } }
            }.onFailure(errorNotifier::notify)
        }
    }

    /**
     * Starts observing active timers for a user (player or GM).
     * Idempotent : ne crée pas de nouvelle souscription si déjà observée pour les mêmes paramètres.
     */
    fun startObservingTimers(
        tableId: String,
        currentUserId: String
    ) {
        if (observeTimersJob?.isActive == true && observingTableId == tableId && observingUserId == currentUserId) {
            return
        }
        observeTimersJob?.cancel()
        observingTableId = tableId
        observingUserId = currentUserId
        observeTimersJob = viewModelScope.launch {
            observeActiveTimersUseCase(
                tableId = tableId,
                currentUserId = currentUserId
            ).collect { timers ->
                _activeTimers.value = timers
            }
        }
    }

    /**
     * Re-sync léger au retour en foreground : refetch sans recréer la souscription Realtime.
     */
    fun refreshOnForeground(tableId: String, currentUserId: String) {
        viewModelScope.launch {
            getActiveTimersUseCase(tableId).onSuccess { timers ->
                _activeTimers.value = timers.filter { it.targetParticipants.contains(currentUserId) }
            }
        }
    }

    fun startObservingForGm(tableId: String) {
        viewModelScope.launch {
            getActiveTimersUseCase(tableId)
                .onSuccess { timers -> _activeTimers.value = timers }
        }
    }

    /**
     * Removes an expired timer from the local list.
     */
    fun removeExpiredTimer(timerId: String) {
        _activeTimers.value = _activeTimers.value.filter { it.id != timerId }
    }

    @OptIn(ExperimentalUuidApi::class)
    fun createGmTimer(durationSeconds: Int, continueAfterEnd: Boolean = false) {
        val timer = GmTimer(
            id = Uuid.random().toString(),
            durationSeconds = durationSeconds,
            remainingSeconds = durationSeconds,
            isPaused = false,
            continueAfterEnd = continueAfterEnd
        )
        _gmTimers.update { it + timer }
    }

    fun pauseGmTimer(timerId: String) {
        _gmTimers.update { timers ->
            timers.map { if (it.id == timerId) it.copy(isPaused = !it.isPaused) else it }
        }
    }

    fun stopGmTimer(timerId: String) {
        _gmTimers.update { it.filter { t -> t.id != timerId } }
    }

    fun tickGmTimer(timerId: String) {
        _gmTimers.update { timers ->
            timers.mapNotNull { t ->
                if (t.id != timerId) return@mapNotNull t
                when {
                    t.overtimeSeconds > 0 ->
                        t.copy(overtimeSeconds = t.overtimeSeconds + 1)
                    t.remainingSeconds - 1 <= 0 && t.continueAfterEnd ->
                        t.copy(remainingSeconds = 0, overtimeSeconds = 1)
                    t.remainingSeconds - 1 <= 0 -> null
                    else -> t.copy(remainingSeconds = t.remainingSeconds - 1)
                }
            }
        }
    }

    /**
     * Resets the launch state.
     */
    fun resetLaunchState() {
        _launchState.value = AsyncUiState.Idle
    }

    override fun onCleared() {
        super.onCleared()
        observeTimersJob?.cancel()
    }
}
